import React, { useCallback, useEffect, useState } from 'react';

import { Button, Divider, Group, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleOff, IconPlus } from '@tabler/icons-react';
import PackageTypeLabel from 'components/PackageTypeLabel';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthletePackages from 'hooks/athletePackage/useAthletePackages';
import { CellProps } from 'react-table';
import { AthletePackageList, AthleteRetrieve } from 'types';
import { formatAthletePackageValidityDisplay } from 'utils/athlete-package.helpers';
import { parseDateTimeToDate } from 'utils/date-functions';

import DrawerAthletePackageCreateForm from '../../Packages/components/AthletePackageCreateForm';
import DrawerAthletePackageDetailView from '../../Packages/components/AthletePackageDetail/AthletePackageDetail';

interface IActivePackagesContentProps {
  athlete?: AthleteRetrieve;
}

const ActivePackagesContent = ({ athlete }: IActivePackagesContentProps): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const [isCreateFormOpened, { open, close }] = useDisclosure(false);
  const [isViewFormOpened, { open: openViewForm, close: closeViewForm }] = useDisclosure(false);
  const [athletePackageTableData, setAthletePackageTableData] = useState<AthletePackageList[]>([]);
  const [athletePackageId, setAthletePackageId] = useState<string | undefined>();
  const {
    data: athletePackageData,
    fetchNextPage: athletePackageFetchNextPage,
    hasNextPage: athletePackageHasNextPage,
    isSuccess: athletePackageIsSuccess,
    isError: athletePackageIsError,
    isLoading: athletePackageIsLoading,
    isFetchingNextPage,
  } = useAthletePackages({
    athlete: athlete?.athleteId,
    statuses: ['ACTIVE', 'HOLD'],
  });
  const { hasPerm, session } = useAuth();
  const athletePackageMutatePerm = hasPerm('AthletePackageMutate');

  const handleAthletePackageClick = useCallback(
    (ap: string): void => {
      setAthletePackageId(ap);
      openViewForm();
    },
    [openViewForm],
  );

  const handleClose = (): void => {
    closeViewForm();
    setAthletePackageId(undefined);
  };

  const athletePackageColumns = [
    {
      Header: 'Status',
      accessor: 'status' as const,
      Cell: ({ value, row }: CellProps<AthletePackageList>) => (
        <PackageStatusBadge
          status={value}
          isCancelEarly={row.original.cancellationDate !== null}
          fullWidth={false}
          data-cy='statusCheck'
        />
      ),
    },
    {
      Header: 'Name',
      accessor: 'package.name',
      Cell: ({ row }: CellProps<AthletePackageList>) => {
        const { child, parent, startDate } = row.original;
        const tz = session?.gym?.timezone;
        return (
          <Stack spacing={2}>
            <Text
              data-cy='activeAthletePackageName'
              weight='bold'
              underline
              sx={{ cursor: 'pointer', width: 'fit-content', wordBreak: 'break-word' }}
              onClick={() => handleAthletePackageClick(row.original.athletePackageId)}
            >
              {row.original.package.name}
            </Text>
            {child && child.isChange && (
              <Text size='xs' c='dimmed' data-cy='changingToIndicator'>
                {`↳ Changing to ${child.packageName} on ${dayjs(
                  parseDateTimeToDate(child.startDate, tz),
                ).format(dateFormat)}`}
              </Text>
            )}
            {parent && parent.isChange && (
              <Text size='xs' c='dimmed' data-cy='changedFromIndicator'>
                {`↳ Changed from ${parent.packageName} on ${dayjs(parseDateTimeToDate(startDate, tz)).format(
                  dateFormat,
                )}`}
              </Text>
            )}
          </Stack>
        );
      },
    },
    {
      Header: 'Type',
      accessor: 'type',
      Cell: ({ row }: CellProps<AthletePackageList>) => (
        <PackageTypeLabel type={row.original.package.type} short data-cy='typeAthletePackage' />
      ),
    },
    {
      Header: 'Validity',
      accessor: 'validity',
      Cell: ({ row }: CellProps<AthletePackageList>) => {
        return (
          <div>
            {formatAthletePackageValidityDisplay(row.original, dateFormat, ' - ', session?.gym?.timezone)}
          </div>
        );
      },
    },
    {
      Header: 'Renew',
      accessor: 'autoRenew',
      Cell: ({ value }: CellProps<AthletePackageList>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.md} color='gray' />
        ),
    },
  ];

  useEffect(() => {
    if (!athletePackageIsLoading && athletePackageData) {
      setAthletePackageTableData(athletePackageData.pages.map(page => page.data.results).flat());
    }
  }, [athletePackageIsLoading, athletePackageData]);

  return (
    <Paper shadow='xl' radius='md' withBorder py='sm' px={0}>
      <Stack spacing={0}>
        <Group position='apart' px='md' pb='sm'>
          <Title order={4}>Current Packages</Title>
          <Button
            compact
            leftIcon={<IconPlus size={theme.fontSizes.md} />}
            onClick={open}
            disabled={!athletePackageMutatePerm}
            data-cy='addAthletePackageButton'
          >
            Package
          </Button>
        </Group>
        <Divider />
        <Table
          tableData={athletePackageTableData}
          columns={athletePackageColumns}
          isSuccess={athletePackageIsSuccess}
          isLoading={athletePackageIsLoading}
          isError={athletePackageIsError}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!athletePackageHasNextPage}
          fetchNextPage={athletePackageFetchNextPage}
          margin={8}
        />
      </Stack>
      <DrawerAthletePackageCreateForm
        athleteId={athlete?.athleteId}
        title={<Title order={3}>Add Package</Title>}
        opened={isCreateFormOpened}
        closeHandler={close}
      />
      <DrawerAthletePackageDetailView
        athletePackageId={athletePackageId}
        setAthletePackageId={setAthletePackageId}
        opened={isViewFormOpened}
        closeHandler={handleClose}
        permission={athletePackageMutatePerm}
      />
    </Paper>
  );
};

export default ActivePackagesContent;
